import './Price.scss';

import cn from 'classnames';
import React, { useMemo } from 'react';

import { useConfig } from '@/common/contexts/ConfigContext';

import { PriceDefaultProps, PricePropTypes } from './Price.proptypes';

const Price = ({
  className,
  context,
  currency,
  emptyLabel,
  quoteNumber,
  source,
  sup,
  type,
  typeSup,
  unit,
  value,
}) => {
  const { isEbat } = useConfig();
  /**
   * Return formatted value
   * @type {number}
   */
  const getNumberValue = useMemo(() => Math.abs(Number(value)), [value]);

  /**
   * Test if value is negative
   * @type {boolean}
   */
  const isNegativeValue = useMemo(() => Number(value) < 0, [value]);

  /**
   * Test if value is not null
   * @type {boolean}
   */
  const isNotNullValue = useMemo(() => {
    if (
      value !== null &&
      isEbat &&
      getNumberValue === 0 &&
      !quoteNumber &&
      source !== 'order'
    ) {
      return false;
    }
    return value !== null && (getNumberValue === 0 || getNumberValue);
  }, [getNumberValue, quoteNumber, source, value, isEbat]);

  /**
   * Test if empty label should be displayed
   * @type {boolean}
   */
  const showEmptyLabel = useMemo(() => emptyLabel && !isNotNullValue, [
    emptyLabel,
    isNotNullValue,
  ]);

  /**
   * Format and return array of value (rounded + decimal)
   * @type {string[]}
   */
  const getArrayValue = useMemo(() => {
    const newValue = [' -- ', ' -- '];

    if (isNotNullValue) {
      const priceSet = getNumberValue.toString().split('');

      newValue[0] = priceSet.slice(0, priceSet.length - 2).join('');
      newValue[1] = priceSet.slice(-2).join('');

      if (getNumberValue < 10) {
        newValue[0] = '0';
        newValue[1] = `0${newValue[1]}`;
      } else if (getNumberValue >= 10 && getNumberValue < 100) {
        newValue[0] = '0';
      }
    }

    return newValue;
  }, [getNumberValue, isNotNullValue]);

  return (
    <span className={cn('price__value', className)} data-qa-id="price-value">
      {showEmptyLabel ? (
        <span className="price__empty" data-qa-id="price-empty">
          {emptyLabel}
        </span>
      ) : (
        <span
          className="price__without-unit"
          data-qa-id={context || 'price-without-unit'}
        >
          {isNegativeValue && '-'}
          {getArrayValue[0]}
          {sup ? (
            <sup className="price__decimal" data-qa-id="price-decimal">
              ,{getArrayValue[1]}
            </sup>
          ) : (
            <>,{getArrayValue[1]}</>
          )}
          &nbsp;{currency}&nbsp;
          {typeSup ? (
            <sup className="price__type" data-qa-id="price-type">
              {type}
            </sup>
          ) : (
            type
          )}
        </span>
      )}
      {unit && !showEmptyLabel && isNotNullValue && (
        <span className="price__unit" data-qa-id="price-unit">
          {unit}
        </span>
      )}
    </span>
  );
};

Price.defaultProps = {
  ...PriceDefaultProps,
};

Price.propTypes = {
  ...PricePropTypes,
};

export default Price;
